import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import {
  ContactMessage,
  ContactMessageStatus,
  listContactMessages,
  updateContactMessageStatus,
} from '../../api/contactMessages';
import { nextSort, SortHeader, sortRows, SortState } from '../tableSort';

const STATUSES: ContactMessageStatus[] = ['New', 'InReview', 'Resolved', 'Archived'];

export function AdminContactMessagesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [sort, setSort] = useState<SortState<'name' | 'status' | 'email' | 'created'>>({ key: 'created', direction: 'desc' });
  const messages = useQuery({
    queryKey: ['admin', 'contact-messages'],
    queryFn: () => listContactMessages(),
  });
  const sortedMessages = sortRows(messages.data ?? [], sort, {
    name: (message) => `${message.firstName} ${message.lastName}`,
    status: (message) => message.status,
    email: (message) => message.email,
    created: (message) => message.createdAtUtc,
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ContactMessageStatus }) =>
      updateContactMessageStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'contact-messages'] }),
  });

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Contact messages</h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            Manage Contact Us requests from Nearrme consumers, stores, and manufacturers.
          </p>
        </div>
      </div>

      <div className="mt-6">
        {messages.isLoading && <LoadingSkeleton className="h-48 w-full" />}
        {messages.error && (
          <Card padded>
            <div className="text-sm text-red-600 dark:text-red-300">
              {(messages.error as Error).message}
            </div>
          </Card>
        )}
        {messages.data && messages.data.length === 0 && (
          <EmptyState title="No messages yet" description="Contact Us submissions will appear here." />
        )}
        {messages.data && messages.data.length > 0 && (
          <Card padded={false} className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Name" sortKey="name" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                    <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Status" sortKey="status" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                    <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Email" sortKey="email" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                    <th className="px-4 py-3 text-slate-700 dark:text-slate-300">Phone</th>
                    <th className="px-4 py-3 text-slate-700 dark:text-slate-300">Message</th>
                    <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Created" sortKey="created" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {sortedMessages.map((message) => (
                    <MessageRow
                      key={message.contactMessageId}
                      message={message}
                      saving={updateStatus.isPending}
                      onOpen={() => navigate(`/admin/contact-messages/${message.contactMessageId}`)}
                      onStatusChange={(status) =>
                        updateStatus.mutate({ id: message.contactMessageId, status })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function MessageRow({
  message,
  saving,
  onOpen,
  onStatusChange,
}: {
  message: ContactMessage;
  saving: boolean;
  onOpen: () => void;
  onStatusChange: (status: ContactMessageStatus) => void;
}) {
  return (
    <tr onClick={(event) => {
      if ((event.target as HTMLElement).closest('select, button, a')) return;
      onOpen();
    }} tabIndex={0} role="link" className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none dark:hover:bg-slate-900 dark:focus:bg-slate-900">
      <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">{message.firstName} {message.lastName}</td>
      <td className="px-4 py-3">
          <select
            value={message.status}
            disabled={saving}
            onChange={(event) => onStatusChange(event.target.value as ContactMessageStatus)}
            className="h-10 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            {STATUSES.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
      </td>
      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{message.email}</td>
      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{message.phone}</td>
      <td className="max-w-md truncate px-4 py-3 text-slate-600 dark:text-slate-400">{message.message}</td>
      <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">{new Date(message.createdAtUtc).toLocaleString()}</td>
    </tr>
  );
}
